import React, { useState, useEffect, useRef, useContext } from 'react';
import AsyncSelect from 'react-select/async';
import { ToastContainer, toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import { Slide } from 'react-toastify';
import axios from 'axios';
import { BASE_URL, GET_ITEM_MASTER, GET_STOCK_CONSUMPTION_REPORT } from '../../utlilities/config';
import withAuth from '../../common/auth';
import { format } from 'date-fns';
import * as XLSX from 'xlsx';
import { TabContext } from '../../components/TabbedLayout';
import { ReportEmbedContext } from '../reportEmbedContext';
import HighlightMetricsModal from './components/HighlightMetricsModal';

// Icons
import {
  FiRefreshCcw, FiDownloadCloud, FiRefreshCw,
  FiAlertCircle, FiCheckCircle, FiPackage,
  FiFileText, FiInfo
} from "react-icons/fi";

// Enhanced Spinner Component
const Spinner = () => (
  <div className="fixed inset-0 z-[60] flex items-center justify-center bg-white bg-opacity-80 backdrop-blur-sm">
    <div className="flex flex-col items-center gap-3">
      <div className="animate-spin rounded-full h-16 w-16 border-t-4 border-b-4 border-indigo-600"></div>
      <p className="text-indigo-600 font-medium">Loading...</p>
    </div>
  </div>
);


// Transaction Type Badge component
const TransactionTypeBadge = ({ type }) => {
  let bgColor, textColor, icon, label;

  // Map transaction type codes to labels
  const typeMap = {
    'SI': 'Sales',
    'SR': 'Sales Return',
    'SA': 'Stock Adjustment',
    'OP': 'Opening Stock Entry',
    'Purchase': 'Purchase',
    'Opening Stock': 'Opening Stock',
    'Closing Stock (Including Post-Dated)': 'Closing Stock',
    'TestEntry': 'Test Entry',
  };

  label = typeMap[type] || type;

  // Set colors and icons based on transaction type
  if (type === 'SI' || type === 'Sales') {
    bgColor = 'bg-blue-100';
    textColor = 'text-blue-800';
    icon = <FiFileText size={14} className="mr-1" />;
  } else if (type === 'SR' || type === 'Sales Return') {
    bgColor = 'bg-orange-100';
    textColor = 'text-orange-800';
    icon = <FiRefreshCw size={14} className="mr-1" />;
  } else if (type === 'SA' || type === 'Stock Adjustment') {
    bgColor = 'bg-purple-100';
    textColor = 'text-purple-800';
    icon = <FiPackage size={14} className="mr-1" />;
  } else if (type === 'OP' || type === 'Opening Stock Entry' || type === 'Opening Stock') {
    bgColor = 'bg-amber-100';
    textColor = 'text-amber-800';
    icon = <FiPackage size={14} className="mr-1" />;
  } else if (type === 'Purchase') {
    bgColor = 'bg-green-100';
    textColor = 'text-green-800';
    icon = <FiCheckCircle size={14} className="mr-1" />;
  } else if (type === 'Closing Stock (Including Post-Dated)' || type === 'Closing Stock') {
    bgColor = 'bg-indigo-100';
    textColor = 'text-indigo-800';
    icon = <FiInfo size={14} className="mr-1" />;
  } else if (type === 'TestEntry' || type === 'Test Entry') {
    bgColor = 'bg-cyan-100';
    textColor = 'text-cyan-800';
    icon = <FiFileText size={14} className="mr-1" />;
  } else {
    bgColor = 'bg-gray-100';
    textColor = 'text-gray-800';
    icon = <FiInfo size={14} className="mr-1" />;
  }

  return (
    <span className={`${bgColor} ${textColor} px-2.5 py-1 rounded-full text-xs font-medium inline-flex items-center`}>
      {icon}
      {label}
    </span>
  );
};

// Custom Select Styles
const customSelectStyles = {
  control: (base) => ({
    ...base,
    minHeight: '42px',
    height: '42px',
    fontSize: '14px',
    borderRadius: '0.5rem',
    border: '1px solid #e2e8f0',
    boxShadow: 'none',
    '&:hover': {
      border: '1px solid #cbd5e1',
    },
  }),
  valueContainer: (base) => ({
    ...base,
    padding: '0 12px',
  }),
  indicatorsContainer: (base) => ({
    ...base,
    height: '42px',
  }),
  menu: (base) => ({
    ...base,
    fontSize: '14px',
    borderRadius: '0.5rem',
    overflow: 'hidden',
    boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)',
    zIndex: 9999,
  }),
  option: (base, state) => ({
    ...base,
    backgroundColor: state.isSelected ? '#4f46e5' : state.isFocused ? '#eef2ff' : 'white',
    color: state.isSelected ? 'white' : '#374151',
    cursor: 'pointer',
    ':active': {
      backgroundColor: '#4f46e5',
      color: 'white',
    },
  }),
  menuPortal: (base) => ({
    ...base,
    zIndex: 9999,
  }),
};

function StockConsumptionBillWise() {
  const tabContext = useContext(TabContext);
  const embedded = useContext(ReportEmbedContext);
  const [loading, setLoading] = useState(false);
  const [consumptionData, setConsumptionData] = useState([]);
  const [itemsMaster, setItemsMaster] = useState([]);
  const [selectedItem, setSelectedItem] = useState(null);
  const [fromDate, setFromDate] = useState(format(new Date(), 'yyyy-MM-dd'));
  const [toDate, setToDate] = useState(format(new Date(), 'yyyy-MM-dd'));
  const [isMetricsModalOpen, setIsMetricsModalOpen] = useState(false);

  const token = localStorage.getItem('accessToken');
  const itemSearchDebounceRef = useRef(null);

  // Pagination state
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(1000);

  // Summary stats
  const [summaryStats, setSummaryStats] = useState({
    totalTransactions: 0,
    totalConsumption: 0,
    lastTransactionDate: null,
    openingStock: 0,
    periodTotalIn: 0,
    periodTotalOut: 0,
    closingStock: 0
  });


  // Initial load - fetch first 1000 items
  useEffect(() => {
    const fetchItems = async () => {
      try {
        const response = await axios.get(`${BASE_URL}${GET_ITEM_MASTER}`, {
          headers: {
            'XApiKey': process.env.REACT_APP_API_KEY,
            'Authorization': `Bearer ${token}`,
            'PageNo': 1,
            'PageSize': 1000,
            'FilterText': '',
            'FilterColumn': '0',
            'FilterValue': '0',
          },
        });

        if (response.status === 200) {
          const masterData = response.data?.data?.masterData || [];
          const pharmacyItems = masterData.filter(
            (item) => (item.ItemType ?? item.itemType) === 'Pharmacy'
          );
          setItemsMaster(pharmacyItems);
        }
      } catch (error) {
        console.error('Failed to fetch items:', error);
        toast.error('Failed to load items', {
          icon: <FiAlertCircle className="text-red-500" />
        });
      }
    };

    if (token) {
      fetchItems();
    }
  }, [token]);

  // Load items from API based on search text (for AsyncSelect)
  const loadItemOptions = (inputValue, callback) => {
    // Clear previous debounce timeout
    if (itemSearchDebounceRef.current) {
      clearTimeout(itemSearchDebounceRef.current);
    }

    // If input is less than 2 characters, return initial items
    if (!inputValue || inputValue.length < 2) {
      const options = (itemsMaster || []).map((item) => ({
        value: item.Item_No ?? item.ItemNo ?? item.id ?? null,
        label: item.Item_Name ?? item.ItemName ?? '',
      }));
      const filteredOptions = inputValue
        ? options.filter(opt => opt.label.toLowerCase().includes(inputValue.toLowerCase()))
        : options;
      callback(filteredOptions);
      return;
    }

    // Debounce API call
    itemSearchDebounceRef.current = setTimeout(async () => {
      try {
        const response = await axios.get(`${BASE_URL}${GET_ITEM_MASTER}`, {
          headers: {
            'XApiKey': process.env.REACT_APP_API_KEY,
            'Authorization': `Bearer ${token}`,
            'PageNo': 1,
            'PageSize': 1000,
            'FilterText': inputValue,
            'FilterColumn': '0',
            'FilterValue': '0',
          },
        });

        if (response.status === 200) {
          const masterData = response.data?.data?.masterData || [];
          const pharmacyItems = masterData.filter(
            (item) => (item.ItemType ?? item.itemType) === 'Pharmacy'
          );

          const options = pharmacyItems.map((item) => ({
            value: item.Item_No ?? item.ItemNo ?? item.id ?? null,
            label: item.Item_Name ?? item.ItemName ?? '',
            ...item,
          }));

          const filteredOptions = inputValue
            ? options.filter(opt => opt.label.toLowerCase().includes(inputValue.toLowerCase()))
            : options;
          callback(filteredOptions);
        } else {
          callback([]);
        }
      } catch (error) {
        console.error('Failed to fetch items:', error);
        callback([]);
      }
    }, 300); // 300ms debounce
  };

  // Get selected item option for AsyncSelect value prop
  const getSelectedItemOption = (itemId) => {
    if (!itemId) return null;

    // First, check if selectedItem exists and matches (most reliable)
    if (selectedItem) {
      const selectedValue = selectedItem.Item_No ?? selectedItem.ItemNo ?? selectedItem.id ?? selectedItem.value;
      if (selectedValue === itemId || String(selectedValue) === String(itemId)) {
        return {
          value: selectedValue,
          label: selectedItem.Item_Name ?? selectedItem.ItemName ?? selectedItem.label ?? '',
        };
      }
    }

    // Fallback: search in itemsMaster
    const item = itemsMaster.find(i => {
      const itemNo = i.Item_No ?? i.ItemNo ?? i.id;
      return itemNo === itemId || String(itemNo) === String(itemId);
    });

    if (item) {
      return {
        value: item.Item_No ?? item.ItemNo ?? item.id ?? null,
        label: item.Item_Name ?? item.ItemName ?? '',
      };
    }

    return null;
  };

  // Cleanup debounce timeout on unmount
  useEffect(() => {
    return () => {
      if (itemSearchDebounceRef.current) {
        clearTimeout(itemSearchDebounceRef.current);
      }
    };
  }, []);

  // Calculate running total based on transaction type
  const calculateRunningTotal = (ledger) => {
    if (!ledger || ledger.length === 0) return [];

    let runningTotal = 0;

    return ledger.map((transaction) => {
      const qty = parseFloat(transaction.Qty) || 0;
      const transType = transaction.TransType || '';

      // Opening Stock sets the initial running total
      if (transType === 'Opening Stock' || transType === 'OP') {
        runningTotal = qty;
      } else if (transType === 'Closing Stock (Including Post-Dated)' || transType === 'Closing Stock') {
        // Closing Stock: Don't update running total, keep previous value
        // runningTotal remains unchanged
      } else {
        // For all other transactions, Qty is already signed correctly:
        // - Sales (SI): Qty is negative (subtracts)
        // - Purchase: Qty is positive (adds)
        // - Sales Return (SR): Qty is positive (adds)
        // - Stock Adjustment (SA): Qty can be positive or negative
        runningTotal += qty;
      }

      // Return transaction with calculated running total
      return {
        ...transaction,
        CalculatedRunningTotal: parseFloat(runningTotal.toFixed(2))
      };
    });
  };

  // Get consumption report data
  const getConsumptionReport = async () => {
    if (!selectedItem) {
      toast.error('Please select an item', {
        autoClose: 2000,
      });
      return;
    }

    setLoading(true);
    try {
      const response = await axios.get(`${BASE_URL}${GET_STOCK_CONSUMPTION_REPORT}`, {
        headers: {
          'XApiKey': process.env.REACT_APP_API_KEY,
          'Authorization': `Bearer ${token}`,
          'itemNo': selectedItem.value,
          'fromDate': fromDate,
          'toDate': toDate,
          'departmentID': localStorage.getItem('department_id') || '',
        },
      });

      if (response.status === 200) {
        const responseData = response.data.data || {};
        const ledger = responseData.ledger || [];
        const summary = responseData.summary || [];

        // Calculate running totals based on transaction types
        const ledgerWithRunningTotal = calculateRunningTotal(ledger);

        // Use ledger data in the same order as received from API (no sorting)
        setConsumptionData(ledgerWithRunningTotal);

        // Calculate summary stats from all data and API summary
        const summaryData = summary[0] || {};
        const lastTransaction = ledger.length > 0 ? ledger[ledger.length - 1] : null;

        setSummaryStats({
          totalTransactions: ledger.length,
          totalConsumption: summaryData.PeriodTotalOut || 0,
          lastTransactionDate: lastTransaction?.TrasactionDate ? format(new Date(lastTransaction.TrasactionDate), 'dd MMM yyyy') : null,
          openingStock: summaryData.OpeningStock || 0,
          periodTotalIn: summaryData.PeriodTotalIn || 0,
          periodTotalOut: summaryData.PeriodTotalOut || 0,
          closingStock: summaryData.ClosingStock || 0
        });
      }
    } catch (error) {
      console.error('Failed to fetch consumption data:', error);
      toast.error('Failed to fetch consumption report', {
        icon: <FiAlertCircle className="text-red-500" />
      });
      setConsumptionData([]);
      setSummaryStats({
        totalTransactions: 0,
        totalConsumption: 0,
        lastTransactionDate: null,
        openingStock: 0,
        periodTotalIn: 0,
        periodTotalOut: 0,
        closingStock: 0
      });
    } finally {
      setLoading(false);
    }
  };

  // Handle item change
  const handleItemChange = (selectedOption) => {
    if (!selectedOption) {
      setSelectedItem(null);
      return;
    }

    // Use selectedOption directly (it contains all item data from AsyncSelect)
    const item = selectedOption;

    // Add to itemsMaster if not already present (for getSelectedItemOption to work)
    if (item) {
      const itemNo = item.Item_No ?? item.ItemNo ?? item.id ?? item.value;
      const existingItem = itemsMaster.find(i => {
        const iNo = i.Item_No ?? i.ItemNo ?? i.id;
        return iNo === itemNo || String(iNo) === String(itemNo);
      });

      if (!existingItem) {
        // Add the full item object to itemsMaster
        setItemsMaster(prev => [...prev, item]);
      }
    }

    setSelectedItem(selectedOption);
  };

  // Format date for display
  const formatDateDisplay = (dateString) => {
    if (!dateString) return '';
    try {
      return format(new Date(dateString), 'dd MMM yyyy');
    } catch {
      return dateString;
    }
  };

  // Use consumptionData directly (no filtering needed)
  const filteredData = consumptionData;

  // Pagination calculations
  const totalPages = Math.ceil(filteredData.length / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const endIndex = startIndex + itemsPerPage;
  const paginatedData = filteredData.slice(startIndex, endIndex);

  // Reset to page 1 when filters change or new data is loaded
  useEffect(() => {
    setCurrentPage(1);
  }, [fromDate, toDate, selectedItem, consumptionData.length]);

  // Export to Excel
  const exportToExcel = () => {
    if (filteredData.length === 0) {
      toast.error('No data to export', {
        autoClose: 2000,
      });
      return;
    }

    try {
      // Prepare data for export
      const exportData = filteredData.map((row, index) => ({
        'S.No': index + 1,
        'Invoice No': row.InvoiceNo || '',
        'Item Name': row.Item_Name || '',
        'Batch Code': row.BatchCode || '',
        'Quantity': Number(row.Qty || 0),
        'Running Total': Number(row.CalculatedRunningTotal || 0),
        'Transaction Date': formatDateDisplay(row.TrasactionDate),
        'Transaction Type': row.TransType || '',
      }));

      const worksheet = XLSX.utils.json_to_sheet(exportData);
      const workbook = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(workbook, worksheet, 'Stock Consumption Report');

      const fileName = `Stock_Consumption_Report_${selectedItem?.label || 'All'}_${fromDate}_to_${toDate}.xlsx`;
      XLSX.writeFile(workbook, fileName);

      toast.success('Report exported successfully', {
        autoClose: 2000,
      });
    } catch (error) {
      console.error('Export error:', error);
      toast.error('Failed to export report', {
        autoClose: 2000,
      });
    }
  };


  const handleExport = () => {
    exportToExcel();
  };

  return (
    <div className="flex h-full bg-background">
      <ToastContainer
        position="top-center"
        autoClose={1000}
        limit={1}
        hideProgressBar={false}
        newestOnTop={false}
        closeOnClick
        rtl={false}
        pauseOnFocusLoss
        draggable
        pauseOnHover
        theme="light"
        transition={Slide}
      />

      {loading && <Spinner />}

      <div className="flex-1 flex flex-col h-full overflow-hidden">
        <header className="shrink-0 border-b border-slate-200 bg-white px-3 py-2.5 mb-2">
          <div className={`flex flex-col gap-3 ${embedded ? 'lg:flex-row lg:justify-end' : 'lg:flex-row lg:items-center lg:justify-between'}`}>
            {!embedded && (
              <div className="min-w-0">
                <p className="text-xs font-semibold uppercase tracking-widest text-indigo-500">
                  Stock Reports
                </p>
                <h1 className="text-lg font-bold text-slate-800">
                  Stock Consumption Report
                </h1>
              </div>
            )}
            <div className="flex flex-wrap items-center justify-end gap-2 sm:gap-3">
              <button
                type="button"
                onClick={getConsumptionReport}
                disabled={!selectedItem || !fromDate || !toDate}
                className="inline-flex items-center gap-2 rounded-full bg-indigo-500 px-5 py-2 text-sm font-semibold text-white shadow-sm transition hover:bg-indigo-600 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <FiRefreshCcw size={16} />
                Generate
              </button>
              <button
                type="button"
                onClick={() => {
                  setSelectedItem(null);
                  setFromDate(format(new Date(), 'yyyy-MM-dd'));
                  setToDate(format(new Date(), 'yyyy-MM-dd'));
                  setConsumptionData([]);
                  setSummaryStats({
                    totalTransactions: 0,
                    totalConsumption: 0,
                    lastTransactionDate: null,
                    openingStock: 0,
                    periodTotalIn: 0,
                    periodTotalOut: 0,
                    closingStock: 0
                  });
                }}
                className="inline-flex items-center gap-2 rounded-full border border-slate-200 px-5 py-2 text-sm font-semibold text-slate-600 shadow-sm transition hover:border-indigo-300 hover:text-indigo-500"
              >
                Reset
              </button>
              <button
                type="button"
                onClick={handleExport}
                className="inline-flex items-center gap-2 rounded-full border border-slate-200 px-5 py-2 text-sm font-semibold text-slate-600 shadow-sm transition hover:border-indigo-300 hover:text-indigo-500"
              >
                <FiDownloadCloud size={16} />
                Export
              </button>
            </div>
          </div>
        </header>

        <main className="flex-1 overflow-hidden flex flex-col">
          <div className="flex flex-col gap-3 flex-1 min-h-0">
            {/* Filters Section */}
            <section className="rounded-2xl border border-slate-200 bg-white p-4 shadow-sm flex-shrink-0">
              <form
                className="grid gap-4 md:grid-cols-2 xl:grid-cols-4"
              >
                <label className="flex flex-col gap-2 text-sm font-medium text-slate-700">
                  Item
                  <div className="[&>div]:!h-auto">
                    <AsyncSelect
                      loadOptions={loadItemOptions}
                      isSearchable
                      menuPortalTarget={document.body}
                      menuPosition="fixed"
                      defaultOptions={true}
                      cacheOptions={true}
                      value={getSelectedItemOption(selectedItem?.value)}
                      onChange={handleItemChange}
                      noOptionsMessage={({ inputValue }) =>
                        inputValue && inputValue.length < 2
                          ? 'Type at least 2 characters to search'
                          : 'No items found'
                      }
                      loadingMessage={() => 'Searching items...'}
                      styles={customSelectStyles}
                      placeholder="Select Item"
                    />
                  </div>
                </label>

                <label className="flex flex-col gap-2 text-sm font-medium text-slate-700">
                  From Date
                  <input
                    type="date"
                    value={fromDate}
                    onChange={(event) => setFromDate(event.target.value)}
                    className="rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm text-slate-700 shadow-sm focus:border-indigo-400 focus:ring-2 focus:ring-indigo-200 transition"
                  />
                </label>

                <label className="flex flex-col gap-2 text-sm font-medium text-slate-700">
                  To Date
                  <input
                    type="date"
                    value={toDate}
                    onChange={(event) => setToDate(event.target.value)}
                    className="rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm text-slate-700 shadow-sm focus:border-indigo-400 focus:ring-2 focus:ring-indigo-200 transition"
                  />
                </label>
              </form>
            </section>

            {/* Metrics Section */}
            <HighlightMetricsModal
              isOpen={isMetricsModalOpen}
              onOpen={() => setIsMetricsModalOpen(true)}
              onClose={() => setIsMetricsModalOpen(false)}
            >
              <div className="grid gap-4 md:grid-cols-4">
                <div className="rounded-md border border-indigo-100 bg-indigo-50/60 p-2 text-slate-700 shadow-sm">
                  <p className="text-xs font-semibold uppercase tracking-wide text-indigo-500">
                    Total Transactions
                  </p>
                  <p className="mt-1 text-md font-semibold text-indigo-600">{summaryStats.totalTransactions}</p>
                </div>
                <div className="rounded-xl border border-indigo-100 bg-indigo-50/60 p-2 text-slate-700 shadow-sm">
                  <p className="text-xs font-semibold uppercase tracking-wide text-indigo-500">
                    Opening Stock
                  </p>
                  <p className="mt-1 text-md font-semibold text-indigo-600">
                    {summaryStats.openingStock.toFixed(2)}
                  </p>
                </div>
                <div className="rounded-xl border border-indigo-100 bg-indigo-50/60 p-2 text-slate-700 shadow-sm">
                  <p className="text-xs font-semibold uppercase tracking-wide text-indigo-500">
                    Closing Stock
                  </p>
                  <p className="mt-1 text-md font-semibold text-indigo-600">
                    {summaryStats.closingStock.toFixed(2)}
                  </p>
                </div>
              </div>
            </HighlightMetricsModal>

            {/* Table Section */}
            <section className="rounded-2xl border border-slate-200 bg-white p-3 shadow-sm flex flex-col flex-1 min-h-0">
              <div className="flex flex-col gap-3 md:flex-row md:items-center md:justify-between flex-shrink-0 mb-4">
                <div className="flex flex-col gap-2">
                  <p className="text-sm text-slate-500">
                    Showing <span className="font-semibold text-slate-700">{startIndex + 1}-{Math.min(endIndex, filteredData.length)}</span> of <span className="font-semibold text-slate-700">{filteredData.length}</span> datas for this report.
                  </p>
                </div>
                {/* Pagination Controls */}
                {totalPages > 1 && (
                  <div className="flex items-center gap-2">
                    <button
                      onClick={() => setCurrentPage(prev => Math.max(1, prev - 1))}
                      disabled={currentPage === 1}
                      className="px-3 py-1.5 rounded-lg border border-slate-200 bg-white text-sm font-medium text-slate-700 disabled:opacity-50 disabled:cursor-not-allowed hover:bg-indigo-50 hover:border-indigo-300 transition-colors"
                    >
                      Previous
                    </button>
                    <div className="flex items-center gap-1">
                      {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                        let pageNum;
                        if (totalPages <= 5) {
                          pageNum = i + 1;
                        } else if (currentPage <= 3) {
                          pageNum = i + 1;
                        } else if (currentPage >= totalPages - 2) {
                          pageNum = totalPages - 4 + i;
                        } else {
                          pageNum = currentPage - 2 + i;
                        }
                        return (
                          <button
                            key={pageNum}
                            onClick={() => setCurrentPage(pageNum)}
                            className={`px-3 py-1.5 rounded-lg border text-sm font-medium transition-colors ${currentPage === pageNum
                              ? 'bg-indigo-600 text-white border-indigo-600'
                              : 'border-slate-200 bg-white text-slate-700 hover:bg-indigo-50 hover:border-indigo-300'
                              }`}
                          >
                            {pageNum}
                          </button>
                        );
                      })}
                    </div>
                    <span className="text-sm text-slate-600">
                      Page {currentPage} of {totalPages}
                    </span>
                    <button
                      onClick={() => setCurrentPage(prev => Math.min(totalPages, prev + 1))}
                      disabled={currentPage === totalPages}
                      className="px-3 py-1.5 rounded-lg border border-slate-200 bg-white text-sm font-medium text-slate-700 disabled:opacity-50 disabled:cursor-not-allowed hover:bg-indigo-50 hover:border-indigo-300 transition-colors"
                    >
                      Next
                    </button>
                  </div>
                )}
              </div>
              <div className="mt-2 flex-1 overflow-auto min-h-0 rounded-xl border border-slate-200">
                <table className="min-w-full divide-y divide-slate-200 text-sm text-slate-700">
                  <thead className="bg-gray-200 sticky top-0 z-10">
                    <tr>
                      <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wide text-slate-500">S.No</th>
                      <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wide text-slate-500">Invoice No</th>
                      <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wide text-slate-500">Item Name</th>
                      <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wide text-slate-500">Batch Code</th>
                      <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wide text-slate-500">Quantity</th>
                      <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wide text-slate-500">Running Total</th>
                      <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wide text-slate-500">Transaction Date</th>
                      <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wide text-slate-500">Transaction Type</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-100 bg-white">
                    {filteredData.length > 0 ? (
                      paginatedData.map((row, index) => (
                        <tr key={startIndex + index} className="hover:bg-indigo-50/30">
                          <td className="whitespace-nowrap px-4 py-3 text-slate-700">
                            {startIndex + index + 1}
                          </td>
                          <td className="whitespace-nowrap px-4 py-3 text-slate-700">
                            {row.InvoiceNo || '-'}
                          </td>
                          <td className="whitespace-nowrap px-4 py-3 text-slate-700">
                            {row.Item_Name || '-'}
                          </td>
                          <td className="whitespace-nowrap px-4 py-3 text-slate-700">
                            {row.BatchCode || '-'}
                          </td>
                          <td className={`whitespace-nowrap px-4 py-3 text-sm font-medium ${parseFloat(row.Qty) < 0 ? 'text-red-600' : 'text-green-600'
                            }`}>
                            {parseFloat(row.Qty).toFixed(2)}
                          </td>
                          <td className={`whitespace-nowrap px-4 py-3 text-sm font-medium ${parseFloat(row.CalculatedRunningTotal || 0) < 0 ? 'text-red-600' : 'text-slate-700'
                            }`}>
                            {parseFloat(row.CalculatedRunningTotal || 0).toFixed(2)}
                          </td>
                          <td className="whitespace-nowrap px-4 py-3 text-slate-700">
                            {formatDateDisplay(row.TrasactionDate)}
                          </td>
                          <td className="whitespace-nowrap px-4 py-3">
                            <TransactionTypeBadge type={row.TransType} />
                          </td>
                        </tr>
                      ))
                    ) : (
                      <tr>
                        <td colSpan={8} className="px-4 py-8 text-center text-slate-500">
                          No data available for the selected period
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>

            </section>
          </div>
        </main>
      </div>
    </div>
  );
}

export default withAuth(StockConsumptionBillWise);

